import { useEffect } from "react";
import { LegalLayout, LegalSection } from "@/components/LegalLayout";
import { Footer } from "@/components/Footer";
import { trackEvent } from "@/lib/analytics";

const List = ({ items }) => (
    <ul className="list-disc space-y-1.5 pl-5">
        {items.map((item) => (
            <li key={item}>{item}</li>
        ))}
    </ul>
);

const Terms = () => {
    useEffect(() => {
        document.title = "Terms of Service | FlowPilot";
        trackEvent("page_view", { page_path: "/terms" });
    }, []);

    return (
        <>
            <LegalLayout
                title="Terms of Service"
                updated="October 2026"
                testId="terms-page"
            >
                <p className="text-base leading-relaxed text-[#4B5563]">
                    These Terms govern your use of the FlowPilot website and any
                    enquiries, consultations, services or software made
                    available through it.
                </p>
                <LegalSection title="1. About FlowPilot" testId="terms-section-about">
                    <p>
                        FlowPilot provides AI-assisted marketing, growth,
                        automation and business-process services designed to
                        help businesses improve lead generation, follow-up,
                        operational efficiency and commercial decision-making.
                    </p>
                </LegalSection>
                <LegalSection title="2. No guarantee of results" testId="terms-section-results">
                    <p>
                        FlowPilot may provide recommendations, automation,
                        advertising support and AI-assisted analysis, but we do
                        not guarantee specific levels of leads, sales, revenue,
                        rankings, advertising performance or other commercial
                        outcomes.
                    </p>
                </LegalSection>
                <LegalSection title="3. Client responsibility" testId="terms-section-responsibilities">
                    <p>Clients remain responsible for:</p>
                    <div className="mt-3">
                        <List
                            items={[
                                "approving advertising budgets and material commercial actions",
                                "ensuring information supplied to FlowPilot is accurate",
                                "complying with applicable laws, industry rules and platform policies",
                                "reviewing important business decisions where human judgement is appropriate",
                            ]}
                        />
                    </div>
                </LegalSection>
                <LegalSection title="4. Third-party platforms" testId="terms-section-platforms">
                    <p>
                        FlowPilot may connect with or rely on third-party
                        services such as Google, Meta, email providers, hosting
                        providers, automation platforms, databases and AI
                        services.
                    </p>
                    <p className="mt-3">
                        Availability, pricing, functionality and policies of
                        these third parties may change and are outside
                        FlowPilot&rsquo;s direct control.
                    </p>
                </LegalSection>
                <LegalSection title="5. Advertising spend" testId="terms-section-spend">
                    <p>
                        Unless specifically agreed otherwise, advertising spend
                        paid to platforms such as Google or Meta is separate
                        from FlowPilot service fees and remains the
                        client&rsquo;s responsibility.
                    </p>
                </LegalSection>
                <LegalSection title="6. AI-assisted services" testId="terms-section-ai">
                    <p>
                        FlowPilot uses artificial intelligence and automated
                        systems to assist with tasks including analysis,
                        campaign preparation, lead qualification, follow-up and
                        recommendations.
                    </p>
                    <p className="mt-3">
                        AI-generated outputs may require human review and should
                        not be treated as professional legal, financial, tax or
                        regulated advice.
                    </p>
                </LegalSection>
                <LegalSection title="7. Intellectual property" testId="terms-section-ip">
                    <p>
                        FlowPilot retains ownership of its software, systems,
                        workflows, methods, prompts, processes and underlying
                        technology unless otherwise agreed in writing.
                    </p>
                    <p className="mt-3">
                        Clients retain ownership of their own business
                        information, branding and materials supplied to
                        FlowPilot.
                    </p>
                </LegalSection>
                <LegalSection title="8. Acceptable use" testId="terms-section-acceptable-use">
                    <p>
                        You must not use FlowPilot services for unlawful,
                        fraudulent, misleading, abusive or harmful activities,
                        or in a way that breaches third-party platform rules.
                    </p>
                </LegalSection>
                <LegalSection title="9. Limitation of liability" testId="terms-section-liability">
                    <p>
                        To the maximum extent permitted by law, FlowPilot will
                        not be liable for indirect or consequential losses, loss
                        of profits, loss of business opportunity, platform
                        suspension, advertising underperformance or losses
                        caused by third-party systems.
                    </p>
                    <p className="mt-3">
                        Nothing in these Terms excludes liability that cannot
                        legally be excluded.
                    </p>
                </LegalSection>
                <LegalSection title="10. Changes to services" testId="terms-section-changes">
                    <p>
                        FlowPilot may update, modify or improve its services,
                        features or pricing from time to time.
                    </p>
                </LegalSection>
                <LegalSection title="11. Termination" testId="terms-section-termination">
                    <p>
                        Either party may stop using or providing services
                        subject to any separate commercial agreement in place
                        between FlowPilot and the client.
                    </p>
                </LegalSection>
                <LegalSection title="12. Governing law" testId="terms-section-law">
                    <p>
                        Unless otherwise agreed in writing, these Terms are
                        governed by the laws of England and Wales.
                    </p>
                </LegalSection>
                <LegalSection title="13. Contact" testId="terms-section-contact">
                    <p>For questions about these Terms, contact:</p>
                    <p className="mt-2">
                        <a
                            href="mailto:admin@flowpilotops.co.uk"
                            data-testid="terms-contact-email"
                            className="font-medium text-[#2563EB] hover:text-blue-700"
                        >
                            admin@flowpilotops.co.uk
                        </a>
                    </p>
                </LegalSection>
            </LegalLayout>
            <Footer />
        </>
    );
};

export default Terms;
